/**
 * Starts the underlying live watch for a query. The cache owns the
 * {@link AbortSignal}; the starter must wire it to the real watch so that
 * aborting it tears the watch down (PowerSync's `.watch(query, handler,
 * { signal })`). Results flow back through `onResult`.
 */
export type WatchStarter<TSpec> = (
  queryKey: string,
  querySpec: TSpec,
  onResult: (data: unknown) => void,
  signal: AbortSignal,
) => void;

type WatchEntry = {
  data: unknown;
  listeners: Set<(data: unknown) => void>;
  controller: AbortController;
};

/**
 * Shares a single live PowerSync watch per query key across all subscribers and
 * tears it down when the last one leaves. Pure coordination logic — the query
 * spec is opaque, so it is unit-testable without a PowerSync runtime.
 */
export class WatchQueryCache<TSpec = unknown> {
  private readonly entries = new Map<string, WatchEntry>();

  constructor(private readonly startWatch: WatchStarter<TSpec>) {}

  subscribe<T>(
    queryKey: string,
    querySpec: TSpec,
    listener: (data: T[]) => void,
  ): () => void {
    let entry = this.entries.get(queryKey);
    if (!entry) {
      // First subscriber — start the shared watch under a fresh AbortController.
      entry = {
        data: null,
        listeners: new Set(),
        controller: new AbortController(),
      };
      this.entries.set(queryKey, entry);
      this.startWatch(
        queryKey,
        querySpec,
        (data) => this.notify(queryKey, data),
        entry.controller.signal,
      );
    } else if (entry.data !== null) {
      // Late subscriber — replay the cached result immediately.
      listener(entry.data as T[]);
    }

    const boxed = listener as (data: unknown) => void;
    entry.listeners.add(boxed);

    return () => this.unsubscribe(queryKey, boxed);
  }

  get activeWatchCount(): number {
    return this.entries.size;
  }

  private notify(queryKey: string, data: unknown): void {
    const entry = this.entries.get(queryKey);
    if (!entry) return;
    entry.data = data;
    entry.listeners.forEach((listener) => listener(data));
  }

  private unsubscribe(
    queryKey: string,
    listener: (data: unknown) => void,
  ): void {
    const entry = this.entries.get(queryKey);
    if (!entry) return;

    entry.listeners.delete(listener);

    if (entry.listeners.size === 0) {
      // Last subscriber left — abort the signal so the underlying watch is
      // actually torn down, then drop the entry so a later subscriber starts
      // a fresh watch.
      entry.controller.abort();
      this.entries.delete(queryKey);
    }
  }
}
